/**
 * Resolve Dispute Modal
 * Shows dispute context and allows dispatcher to select resolution
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useDisputeContext, useResolveDispute, ORDER_STATUS_LABELS } from '@/hooks/useOrders';
import type { DisputeResolution } from '@/lib/api';

interface ResolveDisputeModalProps {
  orderId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolved?: () => void;
}

const RESOLUTION_LABELS: Record<DisputeResolution, string> = {
  FULL_REFUND: 'Полный возврат',
  PARTIAL_REFUND: 'Частичный возврат',
  COMPLETE_AS_IS: 'Завершить как есть',
  CLOSE_NO_ACTION: 'Закрыть без действий',
};

const REASON_LABELS: Record<string, string> = {
  POOR_QUALITY: 'Плохое качество',
  OVERCHARGED: 'Завышенная цена',
  DAMAGE_CAUSED: 'Повреждения',
  MASTER_NO_SHOW: 'Мастер не пришёл',
  CLIENT_NO_SHOW: 'Клиент не на месте',
  PAYMENT_ISSUE: 'Проблема с оплатой',
  SAFETY_CONCERN: 'Небезопасно',
  OTHER: 'Другое',
};

function formatPrice(price: string | null): string {
  if (!price) return '—';
  const num = parseFloat(price);
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ResolveDisputeModal({
  orderId,
  open,
  onOpenChange,
  onResolved,
}: ResolveDisputeModalProps) {
  const [selectedResolution, setSelectedResolution] = useState<DisputeResolution | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [comment, setComment] = useState('');

  const { data: context, isLoading, isError } = useDisputeContext(open ? orderId : null);
  const resolveMutation = useResolveDispute();

  const handleResolve = async () => {
    if (!orderId || !selectedResolution) return;

    try {
      await resolveMutation.mutateAsync({
        orderId,
        resolution: selectedResolution,
        refundAmount: selectedResolution === 'PARTIAL_REFUND' ? parseFloat(refundAmount) : undefined,
        comment: comment.trim() || undefined,
      });

      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      onOpenChange(false);
      resetForm();
      onResolved?.();
    } catch {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    }
  };

  const resetForm = () => {
    setSelectedResolution(null);
    setRefundAmount('');
    setComment('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) resetForm();
    onOpenChange(newOpen);
  };

  const isValid = selectedResolution !== null &&
    (selectedResolution !== 'PARTIAL_REFUND' || (parseFloat(refundAmount) > 0));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Разобрать спор
          </DialogTitle>
          <DialogDescription>
            {context ? `Заказ #${context.order.orderNumber}` : 'Загрузка...'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="text-center py-8 text-destructive">
            Ошибка загрузки данных спора
          </div>
        ) : context ? (
          <div className="space-y-4">
            {/* Dispute Context */}
            <div className="rounded-md border p-3 space-y-2">
              <p className="text-sm font-medium">Информация о споре</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Кто открыл:</span>
                  <p className="font-medium">{context.dispute.disputedBy || '—'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Когда:</span>
                  <p className="font-medium">{formatDate(context.dispute.disputedAt)}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Причина:</span>
                  <p className="font-medium">
                    {REASON_LABELS[context.dispute.disputeReason ?? ''] ?? context.dispute.disputeReason ?? '—'}
                  </p>
                </div>
                {context.dispute.disputeComment && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Комментарий:</span>
                    <p className="font-medium">{context.dispute.disputeComment}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Order Context */}
            <div className="rounded-md border p-3 space-y-2">
              <p className="text-sm font-medium">Контекст заказа</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Статус до спора:</span>
                  <Badge variant="secondary" className="ml-1">
                    {context.order.statusBeforeDispute
                      ? ORDER_STATUS_LABELS[context.order.statusBeforeDispute]
                      : '—'}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Сумма:</span>
                  <p className="font-medium">{formatPrice(context.order.finalPrice)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Оплата:</span>
                  <p className="font-medium">{context.hasPayment ? 'Да' : 'Нет'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Метод:</span>
                  <p className="font-medium">{context.order.paymentMethod || '—'}</p>
                </div>
              </div>
            </div>

            {/* Resolution Selection */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Решение</p>
              <div className="grid gap-2">
                {context.availableResolutions.map((resolution) => (
                  <Button
                    key={resolution}
                    variant={selectedResolution === resolution ? 'default' : 'outline'}
                    size="sm"
                    className="justify-start"
                    onClick={() => setSelectedResolution(resolution)}
                  >
                    {RESOLUTION_LABELS[resolution] ?? resolution}
                  </Button>
                ))}
              </div>
            </div>

            {/* Refund Amount (for PARTIAL_REFUND) */}
            {selectedResolution === 'PARTIAL_REFUND' && (
              <div className="space-y-1">
                <label className="text-sm text-muted-foreground">
                  Сумма возврата (макс. {formatPrice(context.maxRefundAmount)})
                </label>
                <Input
                  type="number"
                  placeholder="Сумма"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  min={1}
                  max={context.maxRefundAmount ? parseFloat(context.maxRefundAmount) : undefined}
                />
              </div>
            )}

            {/* Comment */}
            <div className="space-y-1">
              <label className="text-sm text-muted-foreground">Комментарий к решению</label>
              <Textarea
                placeholder="Обоснование решения..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Отмена
          </Button>
          <Button
            onClick={handleResolve}
            disabled={!isValid || resolveMutation.isPending}
          >
            {resolveMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Применение...
              </>
            ) : (
              'Применить решение'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
